import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Auth ----
  register({ email, name, password, role }) {
    return this.request('/auth/register', {
      method: 'POST',
      body: { email, name, password, ...(role ? { role } : {}) },
    });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  me() {
    return this.request('/auth/me');
  }

  // ---- Users ----
  listUsers({ limit = 100, offset = 0 } = {}) {
    return this.request(`/users?limit=${limit}&offset=${offset}`);
  }
  createUser(payload) {
    return this.request('/users', { method: 'POST', body: payload });
  }
  updateUser(id, payload) {
    return this.request(`/users/${id}`, { method: 'PATCH', body: payload });
  }
  deleteUser(id) {
    return this.request(`/users/${id}`, { method: 'DELETE' });
  }

  // ---- Sites (Site Registry & Asset Onboarding) ----
  listSites({ limit = 100, offset = 0 } = {}) {
    return this.request(`/sites?limit=${limit}&offset=${offset}`);
  }

  createSite(payload) {
    return this.request('/sites', { method: 'POST', body: payload });
  }

  getSite(id) {
    return this.request(`/sites/${id}`);
  }

  listSiteAssets(id) {
    return this.request(`/sites/${id}/assets`);
  }

  // ---- Net-Metering Workflow Tracker ----
  getSiteWorkflow(id) {
    return this.request(`/sites/${id}/workflow`);
  }

  updateSiteTechnical(id, payload) {
    return this.request(`/sites/${id}/workflow/technical`, { method: 'PATCH', body: payload });
  }

  // ---- Deemed-Approval Evidence Assembler ----
  generateEvidencePack(id) {
    return this.request(`/sites/${id}/evidence-pack`, { method: 'POST' });
  }

  listEvidencePacks(id) {
    return this.request(`/sites/${id}/evidence-pack`);
  }

  // ---- Public Readiness Calculators (anonymous) ----
  publicCalculate(payload) {
    return this.request('/public/calculate', { method: 'POST', body: payload });
  }

  // ---- Utility RPS Compliance Console ----
  getRpsConsole(year) {
    return this.request(`/rps/console${year ? `?year=${year}` : ''}`);
  }
  retireRecs(payload) {
    return this.request('/rps/retire', { method: 'POST', body: payload });
  }
  async downloadRpsStatement(year) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/rps/statement${year ? `?year=${year}` : ''}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Failed to export compliance statement (${res.status})`);
    return res.blob();
  }

  // ---- Verifier Verification Workspace ----
  getVerifierFilings() {
    return this.request('/verifier/filings');
  }
  getVerifierLineage(id) {
    return this.request(`/verifier/filings/${id}/lineage`);
  }
  verifyVerifierSignatures(id) {
    return this.request(`/verifier/filings/${id}/verify`, { method: 'POST' });
  }
  assureFiling(id, payload) {
    return this.request(`/verifier/filings/${id}/assure`, { method: 'POST', body: payload });
  }
  async exportVerifierPack(id) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/verifier/filings/${id}/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Failed to export audit package (${res.status})`);
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="(.+?)"/);
    return { blob: await res.blob(), fileName: match ? match[1] : 'lineage-pack.zip' };
  }

  // ---- MRV Reporting & Emissions Inventory ----
  getMrvSites() {
    return this.request('/mrv/sites');
  }
  getMrvFactors() {
    return this.request('/mrv/factors');
  }
  getMrvReports() {
    return this.request('/mrv/reports');
  }
  simulateMrvOcr(fileName) {
    return this.request('/mrv/ocr', { method: 'POST', body: { fileName } });
  }
  compileMrv(payload) {
    return this.request('/mrv/compile', { method: 'POST', body: payload });
  }
  generateMrvPack(id) {
    return this.request(`/mrv/reports/${id}/pack`, { method: 'POST' });
  }
  async downloadMrvPdf(id) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/mrv/reports/${id}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Failed to download MRV pack (${res.status})`);
    return res.blob();
  }

  // ---- REC Payout Control & Revenue Splits ----
  getSettlementsOverview() {
    return this.request('/settlements/overview');
  }
  getSettleablePortfolios() {
    return this.request('/settlements/portfolios');
  }
  calculateSettlement(payload) {
    return this.request('/settlements/calculate', { method: 'POST', body: payload });
  }
  queueSettlement(id) {
    return this.request(`/settlements/${id}/queue`, { method: 'POST' });
  }

  // ---- Milestone & Annuity Billing ----
  getBillingOverview() {
    return this.request('/billing/overview');
  }
  emitTollEvent(payload) {
    return this.request('/billing/toll-events', { method: 'POST', body: payload });
  }
  publishInvoice(id, gateway) {
    return this.request(`/billing/invoices/${id}/publish`, { method: 'POST', body: gateway ? { gateway } : {} });
  }
  triggerInvoiceWebhook(id, payload) {
    return this.request(`/billing/invoices/${id}/webhook`, { method: 'POST', body: payload });
  }

  // ---- Prosumer REC Aggregation / Pooling ----
  getPoolingPortal() {
    return this.request('/pooling/portal');
  }
  authorizeAggregation(signature) {
    return this.request('/pooling/authorize', { method: 'POST', body: { signature } });
  }
  listPortfolios() {
    return this.request('/pooling/portfolios');
  }
  sellPortfolio(id, pricePerRec) {
    return this.request(`/pooling/portfolios/${id}/sell`, { method: 'POST', body: { pricePerRec } });
  }
  settlePortfolio(id) {
    return this.request(`/pooling/portfolios/${id}/settle`, { method: 'POST' });
  }

  // ---- REC Generation & Lineage Ledger ----
  getRecSummary(siteId) {
    return this.request(`/recs/sites/${siteId}/summary`);
  }
  issueRecs(siteId, payload = {}) {
    return this.request(`/recs/sites/${siteId}/issue`, { method: 'POST', body: payload });
  }
  getRecLineage(recId) {
    return this.request(`/recs/${recId}/lineage`);
  }

  // ---- GraphIQ Compliance ----
  listComplianceRules(params = {}) {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null)).toString();
    return this.request(`/compliance/rules${qs ? `?${qs}` : ''}`);
  }
  validateCompliance(payload) {
    return this.request('/compliance/validate', { method: 'POST', body: payload });
  }
  auditCompliance(payload) {
    return this.request('/compliance/audit', { method: 'POST', body: payload });
  }
  createComplianceRule(payload) {
    return this.request('/compliance/rules', { method: 'POST', body: payload });
  }

  // ---- Telemetry Go-Live Wizard ----
  getSiteTelemetry(id) {
    return this.request(`/sites/${id}/telemetry`);
  }
  connectTelemetry(id, payload) {
    return this.request(`/sites/${id}/telemetry/connect`, { method: 'POST', body: payload });
  }
  fetchTelemetrySample(id) {
    return this.request(`/sites/${id}/telemetry/sample`, { method: 'POST' });
  }
  markTelemetryLive(id) {
    return this.request(`/sites/${id}/telemetry/go-live`, { method: 'POST' });
  }
  simulateTelemetryGap(id, payload) {
    return this.request(`/sites/${id}/telemetry/gap`, { method: 'POST', body: payload });
  }
  backfillTelemetry(id) {
    return this.request(`/sites/${id}/telemetry/backfill`, { method: 'POST' });
  }

  /** Fetch the signed PDF as a Blob (Bearer-authenticated). */
  async downloadEvidencePackPdf(id, packId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/sites/${id}/evidence-pack/${packId}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Failed to download evidence pack (${res.status})`);
    return res.blob();
  }
}

export default new ApiService();
